import {Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {OkCancelDialog} from '../../../../components/common/dialogs/okCancelDialog';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getHasRulesDryRun} from '../../../../store/featureFlags/featureFlagsSelectors';
import {SETTINGS_FOOTER_HEIGHT} from '../../../common/settingsWrapper';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {ExpressionDependencies} from '../../../editors/expressions/expressionState';
import {useRuleEditorContext} from '../../../editors/ruleEditor/ruleEditorContext';
import {settingsHorizontalPaddingStyles} from '../../../settingsCommonConstants';
import {trackRuleEvent} from '../../ruleHelpers';
import {EditorStateActions} from '../hooks/useEditorState';
import {RuleExpressionCardsWrapper} from '../ruleExpressionCardsWrapper';
import {RuleEditorState} from '../state/ruleEditorState';
import {RuleEditorSidePanelExpression} from './ruleEditorSidePanelExpression';
import {RuleEditorSidePanelHeader} from './ruleEditorSidePanelHeader';
import {RuleEditorSidePanelNewField} from './ruleEditorSidePanelNewField';

/*
 * Const.
 */

const PANEL_WIDTH = 450;

const TRANSITION_PERIOD = '0.2s';
const TRANSITION_TIMING_FUNCTION = 'ease-in-out';

/*
 * Props.
 */

interface RuleEditorSidePanelProps {
  ruleEditorState: RuleEditorState;
  expressionsDependencies: ExpressionDependencies;
  actions: EditorStateActions;
}

/*
 * Styles.
 */

const StyledSidePanel = styled.aside<{$isOpen: boolean}>`
  width: ${`${PANEL_WIDTH}px`};
  height: 100%;
  background-color: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0px 1px 9px rgba(0, 0, 0, 0.08)`)};
  position: absolute;
  overflow: hidden;
  right: 0;
  top: 0;

  display: grid;
  grid-template-rows: 58px 1fr ${SETTINGS_FOOTER_HEIGHT}px;

  visibility: ${(p) => (p.$isOpen ? 'visible' : 'hidden')};
  transform: translateX(${(p) => (p.$isOpen ? 0 : `${PANEL_WIDTH}px`)});
  transition: transform ${TRANSITION_PERIOD} ${TRANSITION_TIMING_FUNCTION}, visibility ${TRANSITION_PERIOD};
`;

const StyledContentWrapper = styled(RuleExpressionCardsWrapper)`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
`;

const StyledFooter = styled.footer`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};

  ${settingsHorizontalPaddingStyles};
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  align-items: center;
  height: ${SETTINGS_FOOTER_HEIGHT}px;
`;

const StyledLeftButton = styled(Button)`
  margin-right: auto;
`;

/*
 * Component.
 */

export const RuleEditorSidePanel: FC<RuleEditorSidePanelProps> = ({
  ruleEditorState,
  expressionsDependencies,
  actions,
}) => {
  const {selectedExpression, isOpen} = ruleEditorState.expressionPanelState;
  const {isReadOnly, namespace} = useBaseEditorContext();
  const {expressions, expressionCreationState, setExpressionCreationState} = useExpressionsContext();
  const {recipe} = useRuleEditorContext();
  const isEditing = Boolean(
    selectedExpression && expressions.some((exp) => exp.id === selectedExpression.id),
  );
  const [isCloseConfirmationOpen, toggleCloseConfirmation] = useToggle();

  const hasRulesDryRun = useAppSelector(getHasRulesDryRun);
  const shouldDisplayDryRun = Boolean(
    hasRulesDryRun &&
      selectedExpression &&
      selectedExpression.atoms.length > 0 &&
      ruleEditorState.hasDryRunTestData,
  );

  const triggers = useMemo(
    () => ruleEditorState.ruleState.triggers.map((triggerWithCondition) => triggerWithCondition.trigger),
    [ruleEditorState.ruleState],
  );

  const onExpressionSave = () => {
    if (!selectedExpression) {
      return;
    }

    const isExistingExpression = expressions.some((exp) => selectedExpression.id === exp.id);
    const event = isExistingExpression
      ? InterfaceEventsEnum.SETTINGS_DYNAMIC_VARIABLE_EDIT
      : InterfaceEventsEnum.SETTINGS_DYNAMIC_VARIABLE_CREATE;
    trackRuleEvent(event, ruleEditorState.initialRule, namespace, recipe?.id, {
      expression_id: selectedExpression.id,
      type: selectedExpression.inferredType,
      is_default_name: !(
        selectedExpression.name && selectedExpression.name !== selectedExpression.defaultName
      ),
    });

    actions.onSaveDraftExpression();

    expressionCreationState?.onCreateExpression(selectedExpression);
    setExpressionCreationState(undefined);
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledSidePanel $isOpen={isOpen} {...buildInteractionIdProps('rule-editor-expression-panel')}>
      <RuleEditorSidePanelHeader
        onClose={() => actions.onSetPanelOpen(false)}
        isEditing={isEditing}
        onDelete={isEditing ? toggleCloseConfirmation : undefined}
      />
      <StyledContentWrapper>
        {selectedExpression && selectedExpression.atoms.length > 0 ? (
          <RuleEditorSidePanelExpression
            isReadonly={isReadOnly}
            expression={selectedExpression}
            expressionDependencies={expressionsDependencies}
            actions={actions}
          />
        ) : (
          <RuleEditorSidePanelNewField
            onAddExpression={(preExpressionItem) => actions.onAddExpression(preExpressionItem)}
            triggers={triggers}
            expressionDependencies={expressionsDependencies}
          />
        )}
      </StyledContentWrapper>
      <StyledFooter>
        {shouldDisplayDryRun && (
          <StyledLeftButton onClick={actions.onExecDryRun}>
            <Trans id="rule-editor-side-panel-dryrun">Dry run</Trans>
          </StyledLeftButton>
        )}

        {/* TODO: Add close confirmation */}
        <Button onClick={() => actions.onSetPanelOpen(false)}>
          <Trans id="rule-editor-side-panel-cancel">Cancel</Trans>
        </Button>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={!selectedExpression}
          onClick={onExpressionSave}
        >
          <Trans id="rule-editor-side-panel-save">Save</Trans>
        </Button>
      </StyledFooter>

      {isCloseConfirmationOpen && (
        <OkCancelDialog
          title={
            <Trans id="rule-expression-delete-dialog-title">Delete {selectedExpression?.name} variable</Trans>
          }
          okButtonLabel={<Trans id="rule-expression-delete-dialog-confirm">Delete</Trans>}
          onOkClick={() => {
            if (selectedExpression) {
              actions.onDeleteExpression(selectedExpression.id);
            }

            toggleCloseConfirmation();
          }}
          onRequestClose={toggleCloseConfirmation}
          okButtonVariant={ButtonVariantsEnum.DANGER}
        >
          <Trans id="rule-expression-delete-dialog-text">
            Deleting this dynamic variable will remove it from your rule conditions and actions. It will also
            invalidate other variables referencing it.
          </Trans>
        </OkCancelDialog>
      )}
    </StyledSidePanel>
  );
};
